import { useEffect, useState, useCallback, useRef } from 'react';
import { storeService } from '../services/storeService';
import { StoreCatalogItem, StoreCatalogFilter, StoreCategory, StoreColor } from '../models';
import { ProductCard } from '../components/ui/ProductCard';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/ui/Pagination';
import { ColorPickerFilter } from '../components/ui/ColorPickerFilter';
import { CategorySidebar } from '../components/ui/CategorySidebar';
import { VariationFilter } from '../components/ui/VariationFilter';
import { useInitEffect } from '../hooks/useInitEffect';
import { useCatalogFilters, CatalogFilters } from '../hooks/useCatalogFilters';

const PAGE_SIZE = 16;

interface GenderOption {
  label: string;
  value: number | undefined;
}

const GENDER_OPTIONS: GenderOption[] = [
  { label: 'Todos', value: undefined },
  { label: 'Masculino', value: 1 },
  { label: 'Feminino', value: 2 },
  { label: 'Unissex', value: 4 },
];

interface SortOption {
  label: string;
  sortBy?: string;
  sortDirection?: string;
}

const SORT_OPTIONS: SortOption[] = [
  { label: 'Relevância' },
  { label: 'Nome A-Z', sortBy: 'name', sortDirection: 'asc' },
  { label: 'Nome Z-A', sortBy: 'name', sortDirection: 'desc' },
  { label: 'Menor preço', sortBy: 'price', sortDirection: 'asc' },
  { label: 'Maior preço', sortBy: 'price', sortDirection: 'desc' },
  { label: 'Mais recentes', sortBy: 'createdAt', sortDirection: 'desc' },
];

interface SecondaryCatalogFiltersProps {
  categories: StoreCategory[];
  filters: CatalogFilters;
  className?: string;
}

function SecondaryCatalogFilters({ categories, filters, className }: SecondaryCatalogFiltersProps) {
  return (
    <div className={className}>
      {categories.length > 0 && (
        <CategorySidebar
          categories={categories}
          selectedCategoryIds={filters.categoryIds}
          onCategoryChange={filters.handleCategoryChange}
        />
      )}
      <VariationFilter value={filters.variationValue} onChange={filters.handleVariationChange} />
    </div>
  );
}

export function CatalogPage() {
  const [items, setItems] = useState<StoreCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [colors, setColors] = useState<StoreColor[]>([]);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const filters = useCatalogFilters();
  const previousFilterRef = useRef<StoreCatalogFilter | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useInitEffect(() => {
    storeService.getCategories().then(setCategories).catch(() => {});
    storeService.getColors().then(setColors).catch(() => {});
  });

  const fetchCatalog = useCallback(async (filter: StoreCatalogFilter) => {
    setLoading(true);
    try {
      const data = await storeService.getCatalog(filter);
      setItems(data.items);
      setTotal(data.count);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectedSort = SORT_OPTIONS[filters.selectedSortIndex];

  useEffect(() => {
    const currentFilter: StoreCatalogFilter = {
      pageNumber: filters.page,
      pageSize: PAGE_SIZE,
      search: filters.search || undefined,
      categoryIds: filters.categoryIds.length > 0 ? filters.categoryIds : undefined,
      colorId: filters.colorId,
      variationValue: filters.variationValue || undefined,
      gender: filters.gender,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy: selectedSort.sortBy,
      sortDirection: selectedSort.sortDirection,
    };

    const isInitialLoad = previousFilterRef.current === null;

    if (JSON.stringify(currentFilter) !== JSON.stringify(previousFilterRef.current)) {
      previousFilterRef.current = currentFilter;
      fetchCatalog(currentFilter).then(() => {
        if (!isInitialLoad && resultsRef.current) {
          const headerHeight = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
          const top = resultsRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      });
    }
  }, [filters.page, filters.search, filters.categoryIds, filters.colorId, filters.variationValue, filters.gender, filters.minPrice, filters.maxPrice, selectedSort, fetchCatalog]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const activeSecondaryFiltersCount = [
    filters.gender !== undefined,
    filters.colorId !== undefined,
    filters.minPriceInput !== '',
    filters.maxPriceInput !== '',
    filters.categoryIds.length > 0,
    filters.variationValue !== undefined,
  ].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" ref={resultsRef}>
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">

        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              value={filters.searchInput}
              onChange={(e) => filters.handleSearchChange(e.target.value)}
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
            />
            {filters.searchInput && (
              <button
                onClick={() => filters.handleSearchChange('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="relative sm:w-52">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M6 12h12M10 17h4" />
            </svg>
            <select
              value={filters.selectedSortIndex}
              onChange={filters.handleSortChange}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-champagne-dark text-text-dark bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm appearance-none cursor-pointer"
            >
              {SORT_OPTIONS.map((opt, idx) => (
                <option key={opt.label} value={idx}>
                  {opt.label}
                </option>
              ))}
            </select>
            <svg
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {filters.hasFilters && (
            <button
              onClick={filters.clearFilters}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-champagne-dark text-xs text-text-medium hover:text-chocolate hover:border-chocolate transition-colors whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpar filtros
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowMoreFilters((v) => !v)}
          className="sm:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-champagne-dark text-sm text-text-medium mb-3 min-h-[44px]"
        >
          <span className="flex items-center gap-2">
            <svg className="w-4 h-4 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 4h18M6 12h12M10 20h4" />
            </svg>
            Mais filtros
            {activeSecondaryFiltersCount > 0 && (
              <span className="bg-chocolate text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {activeSecondaryFiltersCount}
              </span>
            )}
          </span>
          <svg
            className={`w-4 h-4 text-text-light transition-transform duration-200 ${showMoreFilters ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`${showMoreFilters ? 'flex' : 'hidden'} sm:flex flex-col gap-3`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-light font-medium mr-1">Gênero:</span>
            {GENDER_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => filters.handleGenderChange(opt.value)}
                className={[
                  'px-3 py-1 rounded-full text-xs border transition-colors',
                  filters.gender === opt.value
                    ? 'bg-champagne border-chocolate text-chocolate font-medium'
                    : 'border-champagne-dark text-text-medium hover:border-chocolate/60 hover:text-text-dark',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {colors.length > 0 && (
              <ColorPickerFilter colors={colors} value={filters.colorId} onChange={filters.handleColorChange} />
            )}

            <div className="flex items-center gap-2">
              <span className="text-xs text-text-light whitespace-nowrap">De R$</span>
              <input
                type="number"
                min={0}
                placeholder="0"
                value={filters.minPriceInput}
                onChange={(e) => filters.handleMinPriceChange(e.target.value)}
                className="w-20 px-2 py-1.5 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
              />
              <span className="text-xs text-text-light whitespace-nowrap">Até R$</span>
              <input
                type="number"
                min={0}
                placeholder="0"
                value={filters.maxPriceInput}
                onChange={(e) => filters.handleMaxPriceChange(e.target.value)}
                className="w-20 px-2 py-1.5 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
              />
            </div>
          </div>

          <SecondaryCatalogFilters
            categories={categories}
            filters={filters}
            className="flex flex-col gap-4 md:hidden"
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <SecondaryCatalogFilters
          categories={categories}
          filters={filters}
          className="hidden md:flex md:flex-col gap-4 w-full md:w-64 flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="Nenhum produto encontrado"
              description={filters.hasFilters ? 'Tente ajustar os filtros.' : 'Nenhum produto disponível no momento.'}
            />
          ) : (
            <>
              <p className="text-sm text-text-light mb-4">
                {total} {total === 1 ? 'produto encontrado' : 'produtos encontrados'}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {items.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>

              <Pagination page={filters.page} totalPages={totalPages} onChange={filters.setPage} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
